"use client";

import { useCallback } from "react";
import { TextToPointCloudConfig, CrystalDimensions, WorldCoordinates } from "./types";

/**
 * 生成高斯分布随机数 (Box-Muller变换)
 * 用于更自然的点分布
 */
function gaussianRandom(): number {
  let u = 0, v = 0;
  while(u === 0) u = Math.random(); // 避免0
  while(v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

/**
 * 像素处理模块 (自然化版本)
 * 负责将Canvas ImageData转换为3D点坐标，创造白雪漫反射效果
 */
export function usePixelProcessor() {
  
  /**
   * 验证像素是否为有效的文本像素
   */
  const isValidTextPixel = useCallback((
    r: number,
    g: number,
    b: number,
    alpha: number,
  ): boolean => {
    const brightness = (r + g + b) / 3;
    return brightness > 50 && alpha > 128;
  }, []);

  /**
   * 创建像素索引
   */
  const getPixelIndex = useCallback((x: number, y: number, width: number): number => {
    return (y * width + x) * 4;
  }, []);

  /**
   * 提取像素RGBA值
   */
  const getPixelRGBA = useCallback((
    imageData: ImageData,
    index: number,
  ): [number, number, number, number] => {
    const { data } = imageData;
    return [data[index], data[index + 1], data[index + 2], data[index + 3]];
  }, []);

  /**
   * 计算像素的3D世界坐标 (简化版)
   */
  const calculateWorldCoordinates = useCallback(
    (
      pixelX: number,
      pixelY: number,
      canvasWidth: number,
      canvasHeight: number,
      crystalDimensions: CrystalDimensions,
      textCoverageX: number,
    ): WorldCoordinates => {
      // 智能缩放算法：基于水晶实际尺寸计算目标比例
      const targetRatio = 0.5; // 文字占水晶50%尺寸
      const targetHeight = crystalDimensions.height * targetRatio;
      const targetWidth = targetHeight * (canvasHeight / canvasWidth);

      // 标准化坐标转换（-1到1范围）
      const normalizedX = (pixelX / canvasWidth - 0.5) * 2;
      const normalizedY = ((canvasHeight - pixelY) / canvasHeight - 0.5) * 2;

      // 坐标映射（顺时针90度旋转）+ 覆盖率补偿
      const coverageCompensation = 1.0 / textCoverageX;
      const worldX = normalizedY * (targetWidth / 2) * coverageCompensation;
      const worldY = -normalizedX * (targetHeight / 2) * coverageCompensation;

      return { worldX, worldY, targetWidth, targetHeight };
    },
    [],
  );

  /**
   * 计算进度百分比
   */
  const calculateProgress = useCallback((current: number, total: number): number => {
    return Math.min(100, Math.max(0, (current / total) * 100));
  }, []);

  /**
   * 简化的像素处理函数 - 移除多层、边缘检测、子采样等复杂算法
   */
  const convertImageDataToPoints = useCallback(
    (
      imageData: ImageData & { textCoverageX: number },
      config: TextToPointCloudConfig,
      crystalDimensions: CrystalDimensions,
      signal: AbortSignal,
      onProgress: (progress: number) => void,
    ): Promise<{ points: Float32Array; colors: Float32Array }> => {
      return new Promise((resolve) => {
        if (signal.aborted) return;

        const points: number[] = [];
        const colors: number[] = [];
        const { width, height } = imageData;
        const { pointDensity, depthScale } = config;

        // 获取文本覆盖率信息
        const textCoverageX = imageData.textCoverageX || 1.0;

        // 优化清晰度的采样算法：平衡自然度与清晰度
        const step = Math.max(1, Math.floor(1 / pointDensity));
        const pointsPerPixel = 3; // 减少到3个点，避免过度密集
        const perturbationStrength = 0.6; // 降低扰动强度，保持文字清晰轮廓
        const colorVariation = 0.06; // 适度降低颜色变化，增强一致性
        let processedPixels = 0;

        // 主采样循环：自然化多点采样
        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            if (signal.aborted) return;

            const pixelIndex = getPixelIndex(x, y, width);
            const [r, g, b, alpha] = getPixelRGBA(imageData, pixelIndex);

            // 只处理有效的文本像素
            if (isValidTextPixel(r, g, b, alpha)) {
              // 为每个有效像素生成多个自然分布的点（白雪漫反射效果）
              for (let i = 0; i < pointsPerPixel; i++) {
                // 使用高斯分布创造更自然的扰动
                const gaussianX = gaussianRandom() * (perturbationStrength * 0.3);
                const gaussianY = gaussianRandom() * (perturbationStrength * 0.3);
                const perturbedX = x + gaussianX;
                const perturbedY = y + gaussianY;

                // 计算3D世界坐标
                const { worldX, worldY } = calculateWorldCoordinates(
                  perturbedX,
                  perturbedY,
                  width,
                  height,
                  crystalDimensions,
                  textCoverageX,
                );

                // 平面Z坐标 + 高斯分布的深度扰动 (1.5倍厚度)
                const worldZ = gaussianRandom() * 0.0045; // 增加厚度扰动，增强立体感

                // 添加点坐标
                points.push(worldX, worldY, worldZ);

                // 真实白雪漫反射颜色（高反射率 + 轻微冷色调）
                const baseIntensity = 0.75; // 提高基础白色强度，真实雪的高反射率
                const gaussianVariation = gaussianRandom() * (colorVariation * 0.6);
                
                // 白雪的自然颜色变化范围 (0.5 - 0.95)
                const redIntensity = Math.max(0.5, Math.min(0.95, baseIntensity + gaussianVariation));
                const greenIntensity = Math.max(0.5, Math.min(0.95, baseIntensity + gaussianVariation));
                const blueIntensity = Math.max(0.55, Math.min(1.0, baseIntensity + gaussianVariation + 0.05)); // 轻微蓝色调
                
                colors.push(redIntensity, greenIntensity, blueIntensity);
              }
            }

            // 定期更新进度
            processedPixels++;
            if (processedPixels % 1000 === 0) {
              const totalPixelsToProcess = Math.floor(width / step) * Math.floor(height / step);
              const progressPercent = calculateProgress(processedPixels, totalPixelsToProcess);
              onProgress(progressPercent);
            }
          }
        }

        resolve({
          points: new Float32Array(points),
          colors: new Float32Array(colors),
        });
      });
    },
    [isValidTextPixel, getPixelIndex, getPixelRGBA, calculateWorldCoordinates, calculateProgress],
  );

  return {
    convertImageDataToPoints,
  };
}